import * as React from 'react';
import { Pause, Play, RotateCcw } from 'lucide-react';
import {
  replayLegRecipe,
  replayLegsRecipe,
  type ReplayLegRecipeProps,
} from '../recipes/map.recipe';
import { Button } from './Button';
import { Surface, type SurfaceProps } from './Card';
import { Inline, Stack } from './Layout';
import { Skeleton } from './Skeleton';
import { Slider } from './Slider';
import { Text } from './Text';

export interface RouteReplayLeg {
  phase: NonNullable<ReplayLegRecipeProps['phase']>;
  /** "En route", "On scene", "Towing". */
  label: string;
  /** How long the leg took; also how wide its piece of the strip is. */
  minutes: number;
}

export interface RouteReplayBarProps
  extends Omit<SurfaceProps, 'children' | 'onChange'> {
  /** How far through the call the replay is, 0 to 1. */
  progress: number;
  onProgressChange: (progress: number) => void;
  playing: boolean;
  onTogglePlay: () => void;
  /** Already formatted: when the call was dispatched and when it finished. */
  startLabel: string;
  endLabel: string;
  /** The moment the replay is at: "10:52 AM · On scene". */
  nowLabel: string;
  legs: RouteReplayLeg[];
}

/** The slider's own scale; fine enough that a long call does not step. */
const STEPS = 1000;

/**
 * The bar under the history map that replays a finished call: play, a
 * slider across the whole call, and a strip showing how its time split
 * between driving out, being on scene and towing.
 *
 *   <RouteReplayBar progress={0.4} onProgressChange={seek} playing={playing}
 *     onTogglePlay={toggle} startLabel="10:24 AM" endLabel="11:16 AM"
 *     nowLabel="10:52 AM · On scene" legs={legs}
 *     className="absolute inset-x-4 bottom-4" />
 *
 * The button reads Replay once the end is reached. Where it floats is the
 * map's business, so it takes a className for that and nothing else.
 */
export const RouteReplayBar = React.forwardRef<
  HTMLDivElement,
  RouteReplayBarProps
>(function RouteReplayBar(
  {
    progress,
    onProgressChange,
    playing,
    onTogglePlay,
    startLabel,
    endLabel,
    nowLabel,
    legs,
    ...props
  },
  ref
) {
  const finished = progress >= 1;
  const action = playing ? 'Pause' : finished ? 'Replay' : 'Play';
  const ActionIcon = playing ? Pause : finished ? RotateCcw : Play;
  return (
    <Surface
      ref={ref}
      bordered
      radius="lg"
      padding="sm"
      elevation="overlay"
      {...props}
    >
      <Inline gap={3} align="center">
        <Button
          variant="primary"
          leadingIcon={<ActionIcon />}
          onClick={onTogglePlay}
        >
          {action}
        </Button>
        <Stack gap={2} grow>
          <Inline gap={2} justify="between">
            <Text as="span" variant="caption" tone="muted" numeric>
              {startLabel}
            </Text>
            <Text as="span" variant="caption-strong" numeric aria-live="off">
              {nowLabel}
            </Text>
            <Text as="span" variant="caption" tone="muted" numeric>
              {endLabel}
            </Text>
          </Inline>
          <Slider
            aria-label="Replay position"
            aria-valuetext={nowLabel}
            min={0}
            max={STEPS}
            step={1}
            value={[Math.round(Math.min(1, Math.max(0, progress)) * STEPS)]}
            onValueChange={([value]) => onProgressChange(value / STEPS)}
          />
          <div className={replayLegsRecipe()} aria-hidden="true">
            {legs.map((leg) => (
              <span
                key={leg.phase}
                className={replayLegRecipe({ phase: leg.phase })}
                // The one thing here a class cannot say: each leg's share of
                // the call, which is data.
                style={{ flexGrow: Math.max(leg.minutes, 0), flexBasis: 0 }}
              />
            ))}
          </div>
          <Inline gap={3} wrap>
            {legs.map((leg) => (
              <Text key={leg.phase} as="span" variant="caption" tone="muted">
                {leg.label} {leg.minutes} min
              </Text>
            ))}
          </Inline>
        </Stack>
      </Inline>
    </Surface>
  );
});

export interface RouteReplayBarSkeletonProps
  extends Omit<SurfaceProps, 'children'> {
  /** What is loading, said in words: "Loading the route for this call". */
  label: string;
}

/**
 * The replay bar's own shape, drawn empty, while the route is fetched: the
 * button, the times, the slider and the strip, with the label saying what
 * the wait is for. Same frame and size as the bar, so the swap is a fill.
 *
 *   {route.isLoading && (
 *     <RouteReplayBarSkeleton label="Loading the route for this call"
 *       className="absolute inset-x-4 bottom-4" />
 *   )}
 *
 * Deliberately still, like every skeleton here: no pulse, no shimmer.
 */
export const RouteReplayBarSkeleton = React.forwardRef<
  HTMLDivElement,
  RouteReplayBarSkeletonProps
>(function RouteReplayBarSkeleton({ label, ...props }, ref) {
  return (
    <Surface
      ref={ref}
      bordered
      radius="lg"
      padding="sm"
      elevation="overlay"
      role="status"
      {...props}
    >
      <Inline gap={3} align="center">
        <Skeleton className="h-9 w-20" />
        <Stack gap={2} grow>
          <Inline gap={2} justify="between" align="center">
            <Skeleton shape="text" className="h-3 w-14" />
            <Text as="span" variant="caption-strong" tone="muted">
              {label}
            </Text>
            <Skeleton shape="text" className="h-3 w-14" />
          </Inline>
          <Skeleton shape="pill" className="h-1.5 w-full" />
          <Skeleton shape="pill" className="h-2 w-full" />
          <Inline gap={3}>
            <Skeleton shape="text" className="h-3 w-20" />
            <Skeleton shape="text" className="h-3 w-20" />
            <Skeleton shape="text" className="h-3 w-20" />
          </Inline>
        </Stack>
      </Inline>
    </Surface>
  );
});
